import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import PublicIcon from '@mui/icons-material/Public'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import HomeIcon from '@mui/icons-material/Home'
import { supabase } from '../../config/supabase'
import { Location } from '../../types'

interface LocationWithPath extends Location {
  hierarchy_path?: string
  children_count?: number
}

export default function LocationsList() {
  const navigate = useNavigate()
  const [locations, setLocations] = useState<LocationWithPath[]>([])
  const [filteredLocations, setFilteredLocations] = useState<LocationWithPath[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'region' | 'kommune' | 'city'>('all')

  useEffect(() => {
    fetchLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    filterLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, searchTerm, typeFilter])

  const fetchLocations = async () => {
    try {
      setLoading(true)

      // Fetch all locations
      const { data: locationsData, error: locationsError } = await supabase
        .from('locations')
        .select('*')
        .order('type')
        .order('name')

      if (locationsError) throw locationsError

      // Enhance with hierarchy paths (using PostgreSQL function if available)
      // For now, we'll build it client-side
      const enhanced = (locationsData || []).map((loc: Location) => ({
        ...loc,
        hierarchy_path: buildHierarchyPath(loc, locationsData || []),
      }))

      setLocations(enhanced)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const buildHierarchyPath = (location: Location, allLocations: Location[]): string => {
    const path: string[] = [location.name]
    let current = location

    while (current.parent_id) {
      // eslint-disable-next-line no-loop-func
      const parent = allLocations.find(l => l.id === current.parent_id)
      if (!parent) break
      path.unshift(parent.name)
      current = parent
    }

    return path.join(' > ')
  }

  const filterLocations = () => {
    let filtered = locations

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(l => l.type === typeFilter)
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(
        l =>
          l.name.toLowerCase().includes(search) ||
          l.hierarchy_path?.toLowerCase().includes(search) ||
          l.postal_codes?.some(pc => pc.includes(search))
      )
    }

    setFilteredLocations(filtered)
  }

  const getTypeIcon = (type: string): React.ReactElement | undefined => {
    switch (type) {
      case 'region':
        return <PublicIcon fontSize="small" />
      case 'kommune':
        return <HomeIcon fontSize="small" />
      case 'city':
        return <LocationCityIcon fontSize="small" />
      default:
        return undefined
    }
  }

  const getTypeColor = (type: string): "default" | "primary" | "secondary" | "success" => {
    switch (type) {
      case 'region':
        return 'primary'
      case 'kommune':
        return 'secondary'
      case 'city':
        return 'success'
      default:
        return 'default'
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Lokationer ({filteredLocations.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/locations/new')}
        >
          Tilføj Lokation
        </Button>
      </Box>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Søg på navn, hierarki eller postnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value as any)}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="region">Regioner</MenuItem>
              <MenuItem value="kommune">Kommuner</MenuItem>
              <MenuItem value="city">Byer</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filteredLocations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Ingen lokationer fundet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchTerm ? 'Prøv en anden søgning' : 'Kør populate-location-hierarchy.js scriptet'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Navn</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hierarki</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Postnumre</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Koordinater</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Handling</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLocations.map((location) => (
                <TableRow key={location.id} hover>
                  <TableCell>
                    <Chip
                      {...(getTypeIcon(location.type) && { icon: getTypeIcon(location.type) })}
                      label={location.type}
                      size="small"
                      color={getTypeColor(location.type)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {location.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {location.hierarchy_path || location.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {location.postal_codes && location.postal_codes.length > 0 ? (
                      <Chip
                        label={`${location.postal_codes.length} postnr.`}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {location.latitude && location.longitude ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/locations/edit/${location.id}`)}
                    >
                      Rediger
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

